import { useEffect, useState } from "react";
import type { LibraryHit, StatBlock } from "@ai-rpg/protocol";
import { getBookCreature, searchLibrary } from "../api.ts";
import { LOADING } from "../words.ts";

const SEARCH_DELAY_MS = 250;

interface Props {
  readonly kind: "creature" | "spell";
  readonly label: string;
  /** What happens when nothing is chosen. */
  readonly note: string;
  readonly placeholder: string;
  readonly max: number;
  /** The same entry may be chosen more than once (two of a creature). */
  readonly repeats: boolean;
  readonly maxLevel?: number;
  /** The rules the game will play: decides which printing of an entry is offered. */
  readonly edition: "2014" | "2024";
  readonly chosen: readonly LibraryHit[];
  readonly onChange: (chosen: LibraryHit[]) => void;
}

/** A creature's stat block, fetched when it is opened: the book's text is only sent for the asking. */
export function StatBlockView({ hit, edition }: { hit: LibraryHit; edition: "2014" | "2024" }) {
  const [block, setBlock] = useState<StatBlock | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    let stale = false;
    getBookCreature(hit.key, edition)
      .then((b) => !stale && setBlock(b))
      .catch((err: unknown) => !stale && setProblem(err instanceof Error ? err.message : String(err)));
    return () => {
      stale = true;
    };
  }, [hit.key, edition]);
  if (problem)
    return (
      <p className="problem" role="alert">
        {problem}
      </p>
    );
  if (!block)
    return (
      <p className="quiet" role="status">
        {LOADING}
      </p>
    );
  return (
    <div className="statblock">
      <p className="statblock-summary">{block.summary}</p>
      {block.problem && (
        <p className="problem" role="alert">
          {block.problem}
        </p>
      )}
      <dl className="statblock-lines">
        {block.lines.map((l) => (
          <div key={l.label}>
            <dt>{l.label}</dt>
            <dd className={l.label === "Abilities" ? "figures" : undefined}>{l.value}</dd>
          </div>
        ))}
      </dl>
      {block.sections.map((s) => (
        <section key={s.title}>
          <h4 className="spell-level">{s.title}</h4>
          {s.entries.map((e) => (
            <p key={e.name} className="statblock-entry">
              <strong>{e.name}.</strong> {e.text}
            </p>
          ))}
        </section>
      ))}
    </div>
  );
}

/** Choose entries from the content library by name. Each one says which book it is from. */
export function LibraryPicker({
  kind,
  label,
  note,
  placeholder,
  max,
  repeats,
  maxLevel,
  edition,
  chosen,
  onChange,
}: Props) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<readonly LibraryHit[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // Where the server shows its books' text, a creature can be opened to its stat block.
  const [readable, setReadable] = useState(false);
  const [opened, setOpened] = useState<string | null>(null);
  const stats = kind === "creature" && readable;
  const term = q.trim();
  const full = chosen.length >= max;

  useEffect(() => {
    if (term.length < 2) {
      setHits(null);
      return;
    }
    let stale = false;
    const timer = setTimeout(() => {
      searchLibrary(kind, edition, term, maxLevel)
        .then((r) => {
          if (stale) return;
          setHits(r.hits);
          setReadable(r.text);
          setProblem(null);
        })
        .catch((err: unknown) => {
          if (!stale) setProblem(err instanceof Error ? err.message : String(err));
        });
    }, SEARCH_DELAY_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [kind, term, maxLevel, edition]);

  const add = (hit: LibraryHit) => {
    if (full || (!repeats && chosen.some((c) => c.key === hit.key))) return;
    onChange([...chosen, hit]);
  };
  const offered = (hits ?? []).filter((h) => repeats || !chosen.some((c) => c.key === h.key));

  return (
    <div className="field">
      <label className="field-label" htmlFor={`pick-${kind}`}>
        {label}
        <span className="section-aside">
          {" "}
          {chosen.length} of {max}
        </span>
      </label>

      {chosen.length > 0 ? (
        <ul className="picked">
          {chosen.map((c, i) => (
            <li key={`${c.key}-${i}`} className="picked-row">
              <span className="hit-name">{c.name}</span>
              <span className="hit-detail">{c.detail}</span>
              <span className="hit-book">{c.book}</span>
              <button type="button" className="link" onClick={() => onChange(chosen.filter((_, at) => at !== i))}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="field-note">{note}</p>
      )}

      <input
        id={`pick-${kind}`}
        className="input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        maxLength={60}
        autoComplete="off"
        placeholder={full ? "That's as many as fit." : placeholder}
        disabled={full}
      />
      {problem && (
        <p className="problem" role="alert">
          {problem}
        </p>
      )}
      {hits !== null && !full && (
        <ul className="hits" aria-label={`${label}: matches`}>
          {offered.length === 0 && <li className="quiet hits-none">Nothing by that name in your books.</li>}
          {offered.map((h) => (
            <li key={h.key}>
              <div className="hit-row">
                <button type="button" className="hit" onClick={() => add(h)} title="Add it">
                  <span className="hit-name">{h.name}</span>
                  <span className="hit-detail">{h.detail}</span>
                  <span className="hit-book">{h.book}</span>
                </button>
                {stats && (
                  <button
                    type="button"
                    className="link"
                    aria-expanded={opened === h.key}
                    onClick={() => setOpened(opened === h.key ? null : h.key)}
                  >
                    {opened === h.key ? "Close" : "Stats"}
                  </button>
                )}
              </div>
              {stats && opened === h.key && <StatBlockView hit={h} edition={edition} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
